import axios from "axios";
import type { AxiosCacheInstance } from "axios-cache-interceptor";
import { setupCache, buildWebStorage } from "axios-cache-interceptor";
import queryString from "query-string";

const instance = axios.create();

type CachedAxiosProps = AxiosCacheInstance & {
  clearCache: (url?: string, params?: Record<string, unknown>) => void;
};

/**
 * Use axios with caching for 8 hours
 */
export const cachedAxios = setupCache(instance, {
  ttl: 8 * 60 * 60 * 1000, // 8 hours
  generateKey: (config) => {
    const baseKey = `cache-${config.url}`;
    // If params exist, include them in the cache key
    if (config.params) {
      const serializedParams = queryString.stringify(config.params, {
        arrayFormat: "index",
      });
      return `${baseKey}-${serializedParams}`;
    }
    return baseKey;
  },
  storage: buildWebStorage(localStorage, "axios-cache:"),
}) as CachedAxiosProps;

/**
 * Clear cached data for a specific URL or all cached data.
 *
 * @param url The URL to clear from the cache. If not provided, all cached data will be cleared.
 */
const clearCache = async (url?: string) => {
  if (cachedAxios.storage?.clear) {
    if (url) {
      await cachedAxios.storage.remove(`cache-${url}`);
      return;
    }

    await cachedAxios.storage.clear();
  } else {
    console.error("Cache storage is not available.");
  }
};

cachedAxios.clearCache = clearCache;
